import { z } from 'zod';

// src/metadata-analytics/constants.ts
var METADATA_PATHS = {
  /** EAV row read + its `/timeseries`, `/highlights` and `/views/{id}` sub-routes. */
  analytics: "/metadata-analytics",
  metadata: "/metadata-analytics/metadata",
  timeseries: "/metadata-analytics/timeseries",
  highlights: "/metadata-analytics/highlights",
  analyticsViews: "/metadata-analytics/views",
  /** Explorer reads: the semantic field dictionary, tx stream, facet-value rail. */
  catalog: "/metadata-analytics/catalog",
  transactions: "/metadata-analytics/transactions",
  facetValues: "/metadata-analytics/facet-values",
  /** Build authored views into physical mv_* relations (write). */
  materialize: "/metadata-analytics/materialize",
  /** Config resources (each its own rewrite prefix). */
  facets: "/metadata-facets",
  views: "/metadata-views",
  paths: "/metadata-paths",
  policy: "/metadata-policy",
  dashboards: "/metadata-dashboards",
  resolve: "/metadata-resolve",
  /**
   * Protocol definitions and the per-scope `definition_patch` overlays that
   * retune them (ABIs, address book, confidence weights). One collection for
   * every rung of the ladder — a row's scope is part of its identity, so
   * authoring at a scope is a write to this same resource with a write scope.
   */
  protocols: "/metadata-protocols",
  /**
   * The CONFIDENCE ladder: per-scope floors + what happens below them. Read
   * returns every rung with provenance (not just the resolved answer), because
   * the question a person has is "whose choice is this?". `/preview` is its own
   * route so a dry run can never be mistaken for a save.
   */
  evidencePolicy: "/metadata-evidence-policy",
  evidencePolicyPreview: "/metadata-evidence-policy/preview"
};
var ANALYTICS_SCOPES = ["merged", "system", "customer", "org", "tenant", "user"];
var DEFAULT_ANALYTICS_SCOPE = "merged";
var FILTER_OPS = [
  "eq",
  "neq",
  "in",
  "exists",
  "contains",
  // Range comparisons — for numeric / timestamp columns (e.g. `block_timestamp`).
  // See `ROW_COLUMN_FIELDS` on the back-end: ranges apply to physical columns.
  "gt",
  "gte",
  "lt",
  "lte"
];
var RANGE_FILTER_OPS = ["gt", "gte", "lt", "lte"];
var FILTER_OP_LABELS = {
  eq: "is",
  neq: "is not",
  in: "is any of",
  exists: "exists",
  contains: "contains",
  gt: "greater than",
  gte: "at least",
  lt: "less than",
  lte: "at most"
};
var INTERNAL_ANALYTICS_SCOPES = ["customer", "org"];
var SELF_SERVICE_ANALYTICS_SCOPES = ["merged", "system", "tenant", "user"];
var ANALYTICS_SCOPE_LABELS = {
  merged: "System + my tenant",
  system: "System defaults",
  customer: "Customer",
  org: "Organization",
  tenant: "My tenant",
  user: "My data"
};
var AGGREGATE_METRICS = [
  "count",
  "count_distinct_tx",
  "count_distinct_entity",
  "count_distinct_value",
  "sum",
  "sum_abs",
  "avg",
  "min",
  "max"
];
var NUMERIC_AGGREGATE_METRICS = ["sum", "sum_abs", "avg", "min", "max"];
function isNumericMetric(metric) {
  return NUMERIC_AGGREGATE_METRICS.includes(metric ?? "");
}
var AGGREGATE_METRIC_LABELS = {
  count: "Facts",
  count_distinct_tx: "Transactions",
  count_distinct_entity: "Entities",
  // The distinct VALUES of the facet — "how many different addresses", which is
  // the only one of the four counts that answers that. Pair it with a
  // `canonicalNames` narrowing, or it counts across every fact at once.
  count_distinct_value: "Distinct values",
  // Named for what they do to the value rather than by their SQL function, so a
  // legend reads as a quantity ("Total") and not as an operation ("Sum").
  sum: "Total",
  // Over a SIGNED fact the two differ, and the difference is the point: "Total"
  // of a trade quote is the net position change, and "Volume" is how much moved
  // regardless of direction. A round trip is zero under the first and its full
  // turnover under the second.
  sum_abs: "Volume",
  avg: "Average",
  min: "Minimum",
  max: "Maximum"
};
var AGGREGATE_DIMENSIONS = [
  "canonical_name",
  "chain_id",
  "namespace",
  "source",
  "entity_type",
  "path_id"
];
var CCIP_PROTOCOL_ID = "chainlink_ccip";
var CCIP_FACETS = [
  "message_id",
  "source_chain",
  "dest_chain",
  "fee_token",
  "fee_amount",
  "token_amounts",
  "sender",
  "recipient"
];
var CCIP_VIEW_IDS = {
  message: "mv_ccip_message",
  byDestChain: "mv_ccip_by_dest_chain",
  byFeeToken: "mv_ccip_by_fee_token"
};
var TOKEN_LAUNCH_FACETS = [
  "launch_token",
  "launch_role",
  "launch_quote",
  "launch_pool",
  "token_deployer"
];
var TOKEN_LAUNCH_VIEW_IDS = {
  launch: "mv_token_launch",
  trading: "mv_token_trading",
  /**
   * Per (pool, account, direction) — `metadata_registry.presets.pool_trade_views`.
   *
   * The only one of these three at TRADE grain. `trading` groups on
   * `launch_role`, which is filed per transaction, so a transaction an aggregator
   * routed through several pools is credited to one of them and the pool being
   * read may be another. This groups facts filed per log, which is what lets a
   * pool see all of its own trading and an address's quote in and out be read
   * apart.
   */
  trades: "mv_pool_trades",
  /** The per-leg tape under the roll-up — carries the transaction hash. */
  tradeEvents: "mv_pool_trade_events"
};
var TRADE_SIDES = ["buy", "sell"];
var DEFAULT_ANALYTICS_LIMIT = 100;
var MIN_CONFIDENCE_BOUNDS = { min: 0, max: 1, step: 0.05 };
var CONFIDENCE_PRESETS = { lenient: 0.5, balanced: 0.7, cautious: 0.9 };

// src/metadata-analytics/time.ts
var TIME_FIELDS = ["block_timestamp", "ingested_at"];
var DEFAULT_TIME_FIELD = "block_timestamp";
var TIME_FIELD_LABELS = {
  block_timestamp: "Block time",
  ingested_at: "Ingest time"
};
var TIME_FIELD_HINTS = {
  block_timestamp: "When the transaction happened on-chain",
  ingested_at: "When we processed it \u2014 use this to find a run you just triggered"
};
var TIME_RANGE_PRESETS = [
  "1h",
  "6h",
  "24h",
  "7d",
  "30d",
  "90d",
  "180d",
  "1y"
];
var PRESET_SECONDS = {
  "1h": 3600,
  "6h": 21600,
  "24h": 86400,
  "7d": 604800,
  "30d": 2592e3,
  "90d": 7776e3,
  "180d": 15552e3,
  "1y": 31536e3
};
var PRESET_LABELS = {
  "1h": "Last hour",
  "6h": "Last 6 hours",
  "24h": "Last 24 hours",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  "180d": "Last 180 days",
  "1y": "Last year"
};
function nowSeconds(nowMs) {
  return Math.floor((nowMs ?? Date.now()) / 1e3);
}
function resolveTimeRange(range, nowMs) {
  const now = nowSeconds(nowMs);
  if (range?.preset) {
    return { since: now - PRESET_SECONDS[range.preset], until: now };
  }
  const until = range?.until ?? now;
  const since = range?.since ?? until - PRESET_SECONDS["24h"];
  return { since, until };
}
var NICE_BUCKETS = [
  60,
  // 1m
  300,
  // 5m
  900,
  // 15m
  1800,
  // 30m
  3600,
  // 1h
  10800,
  // 3h
  21600,
  // 6h
  43200,
  // 12h
  86400,
  // 1d
  604800
  // 1w
];
function suggestBucketSeconds(window, targetBuckets = 60) {
  const span = Math.max(1, window.until - window.since);
  const ideal = span / Math.max(1, targetBuckets);
  for (const bucket of NICE_BUCKETS) {
    if (bucket >= ideal) return bucket;
  }
  return NICE_BUCKETS[NICE_BUCKETS.length - 1] ?? 604800;
}

// src/metadata-analytics/query.ts
function filterQuery(filters) {
  return {
    scope: filters.scope,
    canonical_names: filters.canonicalNames,
    value: filters.values,
    where: filters.where,
    tx_id: filters.txId,
    chain_id: filters.chainId,
    since: filters.since,
    until: filters.until,
    // Which clock `since`/`until` apply to (and the sort). Absent => block time.
    time_field: filters.timeField,
    // Only send is_highlight when set (tri-state): undefined => don't filter.
    is_highlight: filters.isHighlight,
    definition_scope: filters.definitionScope,
    min_confidence: filters.minConfidence,
    tenant_id: filters.tenantId
  };
}
function buildMetadataQuery(params = {}) {
  return {
    path: METADATA_PATHS.metadata,
    query: {
      ...filterQuery(params),
      limit: params.limit,
      cursor: params.cursor
    }
  };
}
function buildTimeseriesQuery(params) {
  return {
    path: METADATA_PATHS.timeseries,
    query: {
      ...filterQuery(params),
      bucket: params.bucketSeconds,
      metric: params.metric,
      group_by: params.groupBy,
      limit: params.limit
    }
  };
}
function buildViewReadQuery(viewId, params = {}) {
  const filters = params.filters ?? {};
  const filterPairs = Object.entries(filters).flatMap(
    ([column, value]) => (Array.isArray(value) ? value : [value]).map((v) => `${column}:${v}`)
  );
  return {
    path: `${METADATA_PATHS.analyticsViews}/${encodeURIComponent(viewId)}`,
    query: {
      scope: params.scope,
      filter: filterPairs.length ? filterPairs : void 0,
      order_by: params.orderBy,
      // Only sent alongside a column: `order` on its own would read as a
      // direction the response never applied.
      order: params.orderBy ? params.ascending ? "asc" : "desc" : void 0,
      limit: params.limit,
      tenant_id: params.tenantId
    }
  };
}
function buildHighlightsQuery(params = {}) {
  return {
    path: METADATA_PATHS.highlights,
    query: {
      scope: params.scope,
      protocol_id: params.protocolId,
      tx_id: params.txId,
      chain_id: params.chainId,
      since: params.since,
      until: params.until,
      time_field: params.timeField,
      min_confidence: params.minConfidence,
      limit: params.limit,
      tenant_id: params.tenantId
    }
  };
}
var configPaths = {
  facets: () => METADATA_PATHS.facets,
  facet: (name) => `${METADATA_PATHS.facets}/${encodeURIComponent(name)}`,
  views: () => METADATA_PATHS.views,
  view: (viewId) => `${METADATA_PATHS.views}/${encodeURIComponent(viewId)}`,
  viewsPlan: () => `${METADATA_PATHS.views}/plan`,
  paths: () => METADATA_PATHS.paths,
  path: (pathId) => `${METADATA_PATHS.paths}/${encodeURIComponent(pathId)}`,
  policy: () => METADATA_PATHS.policy,
  dashboards: () => METADATA_PATHS.dashboards,
  dashboard: (dashboardId) => `${METADATA_PATHS.dashboards}/${encodeURIComponent(dashboardId)}`,
  highlights: () => METADATA_PATHS.highlights,
  resolve: () => METADATA_PATHS.resolve,
  protocols: () => METADATA_PATHS.protocols,
  protocol: (protocolId) => `${METADATA_PATHS.protocols}/${encodeURIComponent(protocolId)}`
};
function scopeQuery(scope) {
  return scope ? { scope } : {};
}

// src/metadata-analytics/query-language.ts
var OP_SET = new Set(FILTER_OPS);
var RANGE_OPS = new Set(RANGE_FILTER_OPS);
var RANGE_PREFIXES = [
  [">=", "gte"],
  ["<=", "lte"],
  [">", "gt"],
  ["<", "lt"]
];
var FIELD_RE = /^[A-Za-z_][A-Za-z0-9_.]*$/;
var RELATIVE_TIME_RE = /^now(?:([+-])(\d+)([smhdw]))?$/i;
var RELATIVE_UNIT_SECONDS = {
  s: 1,
  m: 60,
  h: 3600,
  d: 86400,
  w: 604800
};
function tokenize(text) {
  const tokens = [];
  let buf = "";
  let quote = null;
  let depth = 0;
  for (const ch of text) {
    if (quote) {
      buf += ch;
      if (ch === quote) quote = null;
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      buf += ch;
      continue;
    }
    if (ch === "(" || ch === "[") {
      depth += 1;
      buf += ch;
      continue;
    }
    if (ch === ")" || ch === "]") {
      depth = Math.max(0, depth - 1);
      buf += ch;
      continue;
    }
    if (/\s/.test(ch) && depth === 0) {
      if (buf) tokens.push(buf);
      buf = "";
      continue;
    }
    buf += ch;
  }
  if (buf) tokens.push(buf);
  return tokens;
}
function unquote(value) {
  const v = value.trim();
  if (v.length >= 2) {
    const first = v[0];
    if ((first === '"' || first === "'") && v[v.length - 1] === first) {
      return v.slice(1, -1);
    }
  }
  return v;
}
function splitOr(inner) {
  const parts = [];
  let buf = "";
  let quote = null;
  let depth = 0;
  let i = 0;
  const push = () => {
    const value = unquote(buf);
    if (value.length > 0) parts.push(value);
    buf = "";
  };
  while (i < inner.length) {
    const ch = inner[i];
    if (quote) {
      buf += ch;
      if (ch === quote) quote = null;
      i += 1;
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      buf += ch;
      i += 1;
      continue;
    }
    if (ch === "(" || ch === "[") depth += 1;
    else if (ch === ")" || ch === "]") depth = Math.max(0, depth - 1);
    if (depth === 0) {
      if (ch === ",") {
        push();
        i += 1;
        continue;
      }
      const rest = inner.slice(i);
      const or = /^\s+OR\s+/i.exec(rest);
      if (or && buf.trim().length > 0) {
        push();
        i += or[0].length;
        continue;
      }
    }
    buf += ch;
    i += 1;
  }
  push();
  return parts;
}
function parseTimeValue(raw, nowMs) {
  const nowSec = Math.floor((nowMs ?? Date.now()) / 1e3);
  const t = raw.trim();
  if (!t) return null;
  if (t.toLowerCase() === "now") return nowSec;
  const rel = RELATIVE_TIME_RE.exec(t);
  if (rel) {
    if (!rel[1]) return nowSec;
    const sign = rel[1] === "-" ? -1 : 1;
    const mult = RELATIVE_UNIT_SECONDS[rel[3].toLowerCase()] ?? 1;
    return nowSec + sign * Number(rel[2]) * mult;
  }
  if (/^\d+$/.test(t)) {
    const n = Number(t);
    return n > 1e12 ? Math.floor(n / 1e3) : n;
  }
  const ms = Date.parse(t);
  return Number.isNaN(ms) ? null : Math.floor(ms / 1e3);
}
function coerceRangeValue(raw, nowMs) {
  const v = unquote(raw);
  if (/^\d+$/.test(v)) return v;
  const secs = parseTimeValue(v, nowMs);
  return secs != null ? String(secs) : v;
}
function parseExplicit(field, op, rawValue, negate, nowMs) {
  if (op === "exists") {
    if (negate) return { error: "negated exists is not supported" };
    return { filter: { field, op, values: [] } };
  }
  if (negate && op !== "eq" && op !== "neq") {
    return { error: `negation ('-') can't combine with '${op}'` };
  }
  const effectiveOp = negate ? op === "eq" ? "neq" : "eq" : op;
  if (op === "in") {
    const values = splitOr(rawValue);
    if (!values.length) return { error: "'in' needs at least one value" };
    return { filter: { field, op: "in", values } };
  }
  if (RANGE_OPS.has(op)) {
    const v2 = coerceRangeValue(rawValue, nowMs);
    if (!v2) return { error: `'${op}' needs a value` };
    return { filter: { field, op, values: [v2] } };
  }
  const v = unquote(rawValue);
  if (!v) return { error: `'${op}' needs a value` };
  return { filter: { field, op: effectiveOp, values: [v] } };
}
function parseToken(raw, nowMs) {
  let s = raw.trim();
  if (!s) return { error: "empty token" };
  let negate = false;
  if (s.startsWith("-")) {
    negate = true;
    s = s.slice(1);
  }
  const colon = s.indexOf(":");
  if (colon <= 0) return { error: "expected field:value" };
  const field = s.slice(0, colon).trim();
  if (!FIELD_RE.test(field)) return { error: `invalid field '${field}'` };
  const rhs = s.slice(colon + 1);
  if (rhs === "*") {
    if (negate) return { error: "negated exists is not supported" };
    return { filter: { field, op: "exists", values: [] } };
  }
  const nextColon = rhs.indexOf(":");
  if (nextColon !== -1) {
    const maybeOp = rhs.slice(0, nextColon).trim().toLowerCase();
    if (OP_SET.has(maybeOp)) {
      return parseExplicit(field, maybeOp, rhs.slice(nextColon + 1), negate, nowMs);
    }
  }
  if (rhs.startsWith("~")) {
    if (negate) return { error: "negated contains is not supported" };
    const v2 = unquote(rhs.slice(1));
    if (!v2) return { error: "'contains' needs a value" };
    return { filter: { field, op: "contains", values: [v2] } };
  }
  if (rhs.startsWith("(") && rhs.endsWith(")")) {
    if (negate) return { error: "negated group is not supported" };
    const values = splitOr(rhs.slice(1, -1));
    if (!values.length) return { error: "empty '( )' group" };
    return { filter: { field, op: "in", values } };
  }
  if (rhs.startsWith("[") && rhs.endsWith("]")) {
    if (negate) return { error: "negated range is not supported" };
    const parts = rhs.slice(1, -1).split(/\s+TO\s+/i);
    if (parts.length !== 2) return { error: "range must be [a TO b]" };
    const lo = coerceRangeValue(parts[0], nowMs);
    const hi = coerceRangeValue(parts[1], nowMs);
    return {
      filters: [
        { field, op: "gte", values: [lo] },
        { field, op: "lte", values: [hi] }
      ]
    };
  }
  for (const [prefix, op] of RANGE_PREFIXES) {
    if (rhs.startsWith(prefix)) {
      if (negate) return { error: `negated '${op}' is not supported` };
      const v2 = coerceRangeValue(rhs.slice(prefix.length), nowMs);
      if (!v2) return { error: `'${prefix}' needs a value` };
      return { filter: { field, op, values: [v2] } };
    }
  }
  const v = unquote(rhs);
  return { filter: { field, op: negate ? "neq" : "eq", values: [v] } };
}
function parseExplorerQuery(text, opts = {}) {
  const filters = [];
  const errors = [];
  for (const token of tokenize(text)) {
    const result = parseToken(token, opts.nowMs);
    if ("error" in result) errors.push({ token, reason: result.error });
    else if ("filters" in result) filters.push(...result.filters);
    else filters.push(result.filter);
  }
  return { filters, errors };
}
var BOOLEAN_KEYWORDS = /* @__PURE__ */ new Set(["AND", "OR", "NOT"]);
function needsQuote(value) {
  if (value === "") return true;
  if (/\s/.test(value)) return true;
  if (/["']/.test(value)) return true;
  if (value.includes(",")) return true;
  if (BOOLEAN_KEYWORDS.has(value.toUpperCase())) return true;
  if (/^[-*~([\]<>)]/.test(value)) return true;
  const colon = value.indexOf(":");
  if (colon !== -1 && OP_SET.has(value.slice(0, colon).toLowerCase())) return true;
  return false;
}
function quoteQueryValue(value) {
  return needsQuote(value) ? JSON.stringify(value) : value;
}
var quoteValue = quoteQueryValue;
var RANGE_SYMBOL = {
  gt: ">",
  gte: ">=",
  lt: "<",
  lte: "<="
};
function formatFilter(filter) {
  const { field, op } = filter;
  const first = filter.values[0] ?? "";
  switch (op) {
    case "exists":
      return `${field}:*`;
    case "neq":
      return `-${field}:${quoteValue(first)}`;
    case "in":
      return `${field}:(${filter.values.map(quoteValue).join(" OR ")})`;
    case "contains":
      return `${field}:~${quoteValue(first)}`;
    case "gt":
    case "gte":
    case "lt":
    case "lte":
      return `${field}:${RANGE_SYMBOL[op]}${quoteValue(first)}`;
    default:
      return `${field}:${quoteValue(first)}`;
  }
}
function formatExplorerQuery(filters) {
  return filters.map(formatFilter).join(" ");
}
var QueryGrammarError = class extends Error {
};
var KEYWORDS = /* @__PURE__ */ new Set(["AND", "OR", "NOT"]);
function consumeBalanced(text, i, open, close) {
  let depth = 0;
  let quote = null;
  for (; i < text.length; i += 1) {
    const ch = text[i];
    if (quote) {
      if (ch === quote) quote = null;
    } else if (ch === '"' || ch === "'") {
      quote = ch;
    } else if (ch === open) {
      depth += 1;
    } else if (ch === close) {
      depth -= 1;
      if (depth === 0) return i + 1;
    }
  }
  throw new QueryGrammarError(`unbalanced '${open}'`);
}
function consumeQuote(text, i) {
  const quote = text[i];
  for (i += 1; i < text.length; i += 1) if (text[i] === quote) return i + 1;
  throw new QueryGrammarError("unterminated quote");
}
function tokenizeTree(text) {
  const toks = [];
  let buf = "";
  const flush = () => {
    if (!buf) return;
    const up = buf.toUpperCase();
    if (KEYWORDS.has(up)) toks.push({ kind: up, text: buf });
    else if (buf === "-") toks.push({ kind: "NOT", text: "-" });
    else toks.push({ kind: "TERM", text: buf });
    buf = "";
  };
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    if (/\s/.test(ch)) {
      flush();
      i += 1;
    } else if (ch === "(" && (buf === "" || buf === "-")) {
      if (buf === "-") {
        buf = "";
        toks.push({ kind: "NOT", text: "-" });
      }
      toks.push({ kind: "LPAREN", text: "(" });
      i += 1;
    } else if (ch === ")") {
      flush();
      toks.push({ kind: "RPAREN", text: ")" });
      i += 1;
    } else if (ch === "(") {
      const j = consumeBalanced(text, i, "(", ")");
      buf += text.slice(i, j);
      i = j;
    } else if (ch === "[") {
      const j = consumeBalanced(text, i, "[", "]");
      buf += text.slice(i, j);
      i = j;
    } else if (ch === '"' || ch === "'") {
      const j = consumeQuote(text, i);
      buf += text.slice(i, j);
      i = j;
    } else {
      buf += ch;
      i += 1;
    }
  }
  flush();
  return toks;
}
var leaf = (pred) => ({ type: "leaf", pred });
function combine(kind, nodes) {
  if (nodes.length === 1) return nodes[0];
  const flat = [];
  for (const n of nodes) {
    if (n.type === kind) flat.push(...n.children);
    else flat.push(n);
  }
  return { type: kind, children: flat };
}
function termNode(raw, nowMs) {
  let s = raw.trim();
  let negate = false;
  if (s.startsWith("-")) {
    negate = true;
    s = s.slice(1);
  }
  const colon = s.indexOf(":");
  if (colon === -1) {
    const word = unquote(s);
    if (!word) throw new QueryGrammarError(`expected field:value, got '${raw}'`);
    const node = { type: "freetext", word };
    return negate ? { type: "not", child: node } : node;
  }
  if (colon === 0) throw new QueryGrammarError(`expected field:value, got '${raw}'`);
  const res = parseToken(raw, nowMs);
  if ("error" in res) throw new QueryGrammarError(res.error);
  if ("filters" in res) return combine("and", res.filters.map(leaf));
  return leaf(res.filter);
}
var TreeParser = class {
  constructor(toks, nowMs) {
    this.toks = toks;
    this.nowMs = nowMs;
  }
  toks;
  nowMs;
  i = 0;
  peek() {
    return this.i < this.toks.length ? this.toks[this.i].kind : "";
  }
  parse() {
    const node = this.parseOr();
    if (this.i !== this.toks.length) throw new QueryGrammarError(`unexpected '${this.toks[this.i].text}'`);
    return node;
  }
  parseOr() {
    const nodes = [this.parseAnd()];
    while (this.peek() === "OR") {
      this.i += 1;
      nodes.push(this.parseAnd());
    }
    return combine("or", nodes);
  }
  parseAnd() {
    const nodes = [this.parseUnary()];
    for (; ; ) {
      const k = this.peek();
      if (k === "AND") {
        this.i += 1;
        nodes.push(this.parseUnary());
      } else if (k === "TERM" || k === "LPAREN" || k === "NOT") {
        nodes.push(this.parseUnary());
      } else break;
    }
    return combine("and", nodes);
  }
  parseUnary() {
    let negate = false;
    while (this.peek() === "NOT") {
      this.i += 1;
      negate = !negate;
    }
    const node = this.parsePrimary();
    return negate ? { type: "not", child: node } : node;
  }
  parsePrimary() {
    const tok = this.i < this.toks.length ? this.toks[this.i] : null;
    if (tok?.kind === "LPAREN") {
      this.i += 1;
      const node = this.parseOr();
      if (this.peek() !== "RPAREN") throw new QueryGrammarError("expected ')'");
      this.i += 1;
      return node;
    }
    if (tok?.kind === "TERM") {
      this.i += 1;
      return termNode(tok.text, this.nowMs);
    }
    throw new QueryGrammarError(tok ? `unexpected '${tok.text}'` : "unexpected end of query");
  }
};
function parseQuery(text, opts = {}) {
  if (!text || !text.trim()) return { node: null, errors: [] };
  try {
    const toks = tokenizeTree(text);
    if (!toks.length) return { node: null, errors: [] };
    return { node: new TreeParser(toks, opts.nowMs).parse(), errors: [] };
  } catch (err) {
    const reason = err instanceof QueryGrammarError ? err.message : "invalid query";
    return { node: null, errors: [{ token: text.trim(), reason }] };
  }
}
var nodePrec = { or: 1, and: 2, not: 3, leaf: 4, freetext: 4 };
function formatQuery(node) {
  if (!node) return "";
  const wrap = (child, min) => {
    const s = formatQuery(child);
    return nodePrec[child.type] < min ? `(${s})` : s;
  };
  switch (node.type) {
    case "leaf":
      return formatFilter(node.pred);
    case "freetext":
      return quoteValue(node.word);
    case "not":
      return node.child.type === "and" || node.child.type === "or" ? `-(${formatQuery(node.child)})` : `NOT ${wrap(node.child, 3)}`;
    case "and":
      return node.children.map((c) => wrap(c, 2)).join(" ");
    case "or":
      return node.children.map((c) => wrap(c, 1)).join(" OR ");
  }
}
function treeToFilters(node) {
  if (!node) return [];
  const out = [];
  const walk = (n) => {
    if (n.type === "leaf") {
      out.push(n.pred);
      return true;
    }
    if (n.type === "and") return n.children.every(walk);
    return false;
  };
  return walk(node) ? out : null;
}
var NEGATED_OP = {
  eq: "neq",
  neq: "eq",
  gt: "lte",
  lte: "gt",
  gte: "lt",
  lt: "gte"
};
function collectLeafFilters(node) {
  const out = [];
  const walk = (n, negated) => {
    if (n.type === "leaf") {
      if (!negated) {
        out.push(n.pred);
        return;
      }
      const op = NEGATED_OP[n.pred.op];
      if (op) out.push({ ...n.pred, op });
      return;
    }
    if (n.type === "not") {
      walk(n.child, !negated);
      return;
    }
    if (n.type === "and" || n.type === "or") {
      for (const child of n.children) walk(child, negated);
    }
  };
  if (node) walk(node, false);
  return out;
}
function queryTextToExplorer(text, opts = {}) {
  const { node, errors } = parseQuery(text, opts);
  if (errors.length) return { filters: [], errors };
  if (node === null) return { filters: [], q: void 0, errors: [] };
  const chips = treeToFilters(node);
  if (chips !== null) return { filters: chips, q: void 0, errors: [] };
  return { filters: collectLeafFilters(node), q: formatQuery(node), errors: [] };
}
function appendAndClause(q, filter) {
  const clause = formatFilter(filter);
  const base = q.trim();
  if (!base) return clause;
  const { node } = parseQuery(base);
  const wrapped = node?.type === "or" ? `(${base})` : base;
  return `${wrapped} ${clause}`;
}

// src/metadata-analytics/explorer.ts
var EMPTY_EXPLORER_QUERY = { filters: [] };
function encodeFilter(filter) {
  if (filter.op === "exists") return `${filter.field}:exists`;
  const value = filter.op === "in" ? filter.values.join(",") : filter.values[0] ?? "";
  return `${filter.field}:${filter.op}:${value}`;
}
function decodeFilter(raw) {
  const first = raw.indexOf(":");
  const field = (first === -1 ? raw : raw.slice(0, first)).trim();
  if (!field) return null;
  if (first === -1) return null;
  const rest = raw.slice(first + 1);
  const second = rest.indexOf(":");
  const op = (second === -1 ? rest : rest.slice(0, second)).trim();
  if (!FILTER_OPS.includes(op)) return null;
  const value = second === -1 ? "" : rest.slice(second + 1);
  if (op === "exists") return { field, op, values: [] };
  if (op === "in") return { field, op, values: value.split(",").map((v) => v.trim()).filter(Boolean) };
  return { field, op, values: [value] };
}
function whereParams(filters) {
  const encoded = filters.map(encodeFilter);
  return encoded.length ? encoded : void 0;
}
function filterParams(query) {
  if (query.q && query.q.trim()) return { q: query.q.trim() };
  return { where: whereParams(query.filters) };
}
function ambientQuery(query) {
  const out = {
    scope: query.scope,
    chain_id: query.chainId,
    min_confidence: query.minConfidence,
    definition_scope: query.definitionScope,
    tenant_id: query.tenantId,
    time_field: query.timeField
  };
  if (query.timeRange) {
    const window = resolveTimeRange(query.timeRange);
    out.since = window.since;
    out.until = window.until;
  }
  return out;
}
function buildCatalogQuery(scope) {
  return { path: METADATA_PATHS.catalog, query: { scope } };
}
function buildTransactionsQuery(query, opts = {}) {
  return {
    path: METADATA_PATHS.transactions,
    query: {
      ...ambientQuery(query),
      ...filterParams(query),
      limit: opts.limit,
      cursor: opts.cursor
    }
  };
}
function buildFacetValuesQuery(field, query, opts = {}) {
  return {
    path: METADATA_PATHS.facetValues,
    query: {
      ...ambientQuery(query),
      field,
      ...filterParams(query),
      limit: opts.limit
    }
  };
}
function buildTransactionDetailQuery(txId, query = {}, opts = {}) {
  return {
    path: METADATA_PATHS.metadata,
    query: {
      tx_id: txId,
      scope: query.scope,
      min_confidence: query.minConfidence,
      tenant_id: query.tenantId,
      limit: opts.limit ?? 500
    }
  };
}
function explorerQueryToSearchParams(query) {
  const params = new URLSearchParams();
  if (query.scope) params.set("scope", query.scope);
  if (query.chainId) params.set("chain_id", query.chainId);
  if (query.minConfidence != null) params.set("min_confidence", String(query.minConfidence));
  if (query.definitionScope) params.set("definition_scope", query.definitionScope);
  if (query.tenantId) params.set("tenant_id", query.tenantId);
  if (query.timeField) params.set("time_field", query.timeField);
  if (query.timeRange?.preset) params.set("range", query.timeRange.preset);
  else if (query.timeRange) {
    if (query.timeRange.since != null) params.set("since", String(query.timeRange.since));
    if (query.timeRange.until != null) params.set("until", String(query.timeRange.until));
  }
  if (query.q && query.q.trim()) params.set("q", query.q.trim());
  else for (const filter of query.filters) params.append("where", encodeFilter(filter));
  return params;
}
function explorerQueryFromSearchParams(params) {
  const rawQ = params.get("q");
  const parsed = rawQ ? queryTextToExplorer(rawQ) : null;
  const filters = parsed ? parsed.filters : params.getAll("where").map(decodeFilter).filter((f) => f !== null);
  const q = parsed && !parsed.errors.length ? parsed.q : rawQ || void 0;
  const num = (key) => {
    const raw = params.get(key);
    if (raw == null || raw === "") return void 0;
    const parsed2 = Number(raw);
    return Number.isFinite(parsed2) ? parsed2 : void 0;
  };
  const preset = params.get("range");
  const since = num("since");
  const until = num("until");
  const timeRange = preset ? { preset } : since != null || until != null ? { since, until } : void 0;
  const rawTimeField = params.get("time_field");
  const timeField = TIME_FIELDS.includes(rawTimeField) ? rawTimeField : void 0;
  const scopeParam = (key) => {
    const raw = params.get(key);
    return raw && ANALYTICS_SCOPES.includes(raw) ? raw : void 0;
  };
  return {
    scope: scopeParam("scope"),
    chainId: params.get("chain_id") || void 0,
    minConfidence: num("min_confidence"),
    definitionScope: scopeParam("definition_scope"),
    tenantId: params.get("tenant_id") || void 0,
    timeRange,
    timeField,
    filters,
    q
  };
}
function withFilter(query, filter) {
  if (query.q && query.q.trim()) {
    const q = appendAndClause(query.q, filter);
    const parsed = queryTextToExplorer(q);
    if (parsed.errors.length) return { ...query, q };
    return { ...query, filters: parsed.filters, q: parsed.q };
  }
  const rest = query.filters.filter((f) => !(f.field === filter.field && f.op === filter.op));
  return { ...query, filters: [...rest, filter], q: void 0 };
}
function withoutFilter(query, index) {
  return { ...query, filters: query.filters.filter((_, i) => i !== index), q: void 0 };
}
function equalsFilter(field, value) {
  return { field, op: "eq", values: [value] };
}
function definitionOverrideScope(row) {
  const scope = row.definition_scope;
  if (!scope || scope === "system" || scope === "merged") return null;
  return ANALYTICS_SCOPES.includes(scope) ? scope : null;
}
function facetCreateBody(field, opts = {}) {
  return {
    name: field.name || field.canonical_name,
    canonical_name: field.canonical_name,
    canonical_type: field.type || "string",
    visibility: opts.visibility ?? "tenant",
    indexing: opts.indexing ?? "indexed",
    highlight: opts.highlight ?? false,
    description: field.label,
    enabled: true,
    ...field.protocol_id ? { namespaces: [field.protocol_id] } : {}
  };
}
function viewCreateBody(opts) {
  return {
    view_id: opts.viewId,
    facet_names: opts.facetNames,
    ...opts.scope ? { scope: opts.scope } : {},
    ...opts.source ? { source: opts.source } : {},
    ...opts.protocolId ? { protocol_id: opts.protocolId } : {},
    ...opts.groupBy ? { group_by: opts.groupBy } : {}
  };
}
function materializeRequest(viewId, scope) {
  return {
    path: METADATA_PATHS.materialize,
    query: { view_id: viewId, scope }
  };
}

// src/metadata-analytics/render.ts
function unwrapValue(value) {
  if (value === null || value === void 0) return value;
  if (typeof value === "object") {
    const o = value;
    const preferred = o.display ?? o.formatted ?? o.value ?? o.address ?? o.symbol ?? o.name;
    if (typeof preferred === "string" || typeof preferred === "number" || typeof preferred === "boolean") {
      return preferred;
    }
  }
  return value;
}
function shortenHex(value, lead = 6, tail = 4) {
  if (!value.startsWith("0x") || value.length <= lead + tail + 2) return value;
  return `${value.slice(0, lead + 2)}\u2026${value.slice(-tail)}`;
}
function renderFieldValue(value, kind) {
  const scalar = unwrapValue(value);
  if (scalar === null || scalar === void 0 || scalar === "") return "\u2014";
  const text = typeof scalar === "string" ? scalar : stringify(scalar);
  switch (kind) {
    case "address":
    case "message_id":
    case "tx_hash":
    case "hash":
      return shortenHex(text);
    default:
      return text;
  }
}
function stringify(value) {
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
function humanizeFieldName(name) {
  const spaced = name.replace(/[_.]+/g, " ").trim();
  if (!spaced) return name;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
var PANEL_KINDS = ["timeseries", "toplist", "kpi", "table", "ledger", "distribution", "highlights"];
var PANEL_SOURCES = ["timeseries", "metadata", "view", "highlights"];
var TimeRangeSchema = z.object({
  preset: z.enum(TIME_RANGE_PRESETS).optional(),
  since: z.number().int().nonnegative().optional(),
  until: z.number().int().nonnegative().optional()
}).describe("A rolling preset or an absolute [since, until) unix-second window.");
var PanelFilterSchema = z.object({
  field: z.string().min(1),
  op: z.enum(FILTER_OPS),
  values: z.array(z.string()).default([])
});
var PanelQuerySchema = z.object({
  source: z.enum(PANEL_SOURCES),
  // Shared filters.
  canonicalNames: z.array(z.string()).optional(),
  values: z.array(z.string()).optional(),
  /**
   * Structured explorer chips (the `timeseries` / `metadata` sources honor them;
   * `view` / `highlights` ignore them). This is what an explorer view exports.
   */
  where: z.array(PanelFilterSchema).optional(),
  txId: z.string().optional(),
  chainId: z.string().optional(),
  /**
   * Which clock this panel's window applies to; the viewer's axis control
   * overrides it board-wide. Absent means block time — when the transaction
   * happened rather than when we processed it.
   */
  timeField: z.enum(TIME_FIELDS).optional(),
  isHighlight: z.boolean().optional(),
  minConfidence: z.number().gte(0).lte(1).optional(),
  /** Read scope; the viewer's scope control overrides it board-wide. */
  scope: z.enum(ANALYTICS_SCOPES).optional(),
  // timeseries.
  metric: z.enum(AGGREGATE_METRICS).optional(),
  groupBy: z.enum(AGGREGATE_DIMENSIONS).optional(),
  /** Fixed bucket width; when unset the panel auto-sizes to its window. */
  bucketSeconds: z.number().int().positive().optional(),
  // view.
  viewId: z.string().optional(),
  viewFilters: z.record(z.string(), z.union([z.string(), z.array(z.string())])).optional(),
  /**
   * Rank the view's rows on one of its own columns.
   *
   * Matters most where `limit` is doing real work: a bare LIMIT is not a "top N"
   * or a "latest N", because SQL leaves which rows survive entirely to the
   * engine. A tape headed "newest first" without this shows an arbitrary hundred
   * and calls them recent.
   */
  orderBy: z.string().optional(),
  /** Descending by default when `orderBy` is set — "biggest"/"newest" first. */
  descending: z.boolean().optional(),
  // highlights.
  protocolId: z.string().optional(),
  // paging.
  limit: z.number().int().positive().optional()
});
var PanelPositionSchema = z.object({
  x: z.number().int().nonnegative().default(0),
  y: z.number().int().nonnegative().default(0),
  w: z.number().int().positive().default(4),
  h: z.number().int().positive().default(3)
});
var DashboardPanelSchema = z.object({
  id: z.string().min(1),
  title: z.string().default(""),
  kind: z.enum(PANEL_KINDS),
  query: PanelQuerySchema,
  position: PanelPositionSchema.default({ x: 0, y: 0, w: 4, h: 3 }),
  /** Optional per-panel range; falls back to the dashboard range when unset. */
  timeRange: TimeRangeSchema.optional(),
  /** Optional prose the panel renders as a caption. */
  description: z.string().optional()
});
var TemplateVariableSchema = z.object({
  /** Referenced in panel queries as `$name` (bare word: letters/digits/_). */
  name: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]*$/, "variable name must be a bare word"),
  /** The field the value filters on (`chain_id`, `protocol`, `dest_chain`, …). */
  field: z.string().min(1),
  /** Human label for the picker; falls back to the name. */
  label: z.string().optional(),
  /** Seed value; `*` (or unset) means "All" — the substitution drops the chip. */
  default: z.string().optional(),
  /** Allow selecting several values (compiles to an `in` chip). */
  multi: z.boolean().default(false),
  /**
   * Names of the variables that SCOPE this one's value list (Grafana's chained
   * variables). A picker offers only values that survive its dependencies'
   * current selections, so choosing a token leaves the pool picker showing that
   * token's pools rather than the warehouse's thirty busiest.
   *
   * Ordering, not just filtering: dependencies flow one way, so picking a pool
   * never shrinks the token list above it. That asymmetry is the point — a list
   * that narrowed in both directions would make an unreachable combination look
   * like a missing one, and there'd be no value left to widen from.
   *
   * The narrowing is only as honest as the grain the back-end correlates at; for
   * the per-trade facts that is per-log, so a routed transaction's second pool
   * doesn't leak into the first pool's token (see the API's `_correlated`).
   */
  dependsOn: z.array(z.string()).default([]),
  /**
   * The field this variable's chip uses when it SCOPES a dependent's value list,
   * when that differs from the field its own list reads. Defaults to `field`.
   *
   * One dimension is often named by two facts at two grains. A token is
   * `launch_token` on the transaction that launched it and `trade_token` on each
   * leg that traded it, and the picker wants both: it must LIST launched tokens
   * (including one that never traded — a launch board's whole subject) while
   * SCOPING per leg, since a chip mixing grains falls back to correlating across
   * the whole transaction and re-admits the routed trade's other pool.
   */
  scopeField: z.string().optional()
});
var TEMPLATE_VARIABLE_ALL = "*";
var DashboardSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  /** Ladder rung the dashboard is authored at (system for a preset). */
  scope: z.enum(["system", "customer", "org", "tenant", "user"]).default("tenant"),
  timeRange: TimeRangeSchema.default({ preset: "24h" }),
  /** Board-level `$name` parameters (Datadog template variables). */
  templateVariables: z.array(TemplateVariableSchema).default([]),
  panels: z.array(DashboardPanelSchema).default([]),
  readonly: z.boolean().default(false),
  version: z.number().int().nonnegative().default(1)
});
function parseDashboard(value) {
  return DashboardSchema.parse(value);
}
var BUCKETED_KINDS = /* @__PURE__ */ new Set(["timeseries"]);
function defaultBucketSeconds(kind, window) {
  if (BUCKETED_KINDS.has(kind)) return suggestBucketSeconds(window);
  return Math.max(1, window.until - window.since);
}
function buildPanelQuery(panel, dashboardRange) {
  const window = resolveTimeRange(panel.timeRange ?? dashboardRange);
  const q = panel.query;
  const filters = {
    canonicalNames: q.canonicalNames,
    values: q.values,
    // Encode the structured chips to the repeated `where=` tokens the reads
    // parse (undefined when there are none, so the URL stays clean).
    where: whereParams(q.where ?? []),
    txId: q.txId,
    chainId: q.chainId,
    isHighlight: q.isHighlight,
    minConfidence: q.minConfidence,
    scope: q.scope,
    since: window.since,
    until: window.until,
    timeField: q.timeField
  };
  if (q.source === "timeseries") {
    return buildTimeseriesQuery({
      ...filters,
      bucketSeconds: q.bucketSeconds ?? defaultBucketSeconds(panel.kind, window),
      metric: q.metric,
      groupBy: q.groupBy,
      limit: q.limit
    });
  }
  if (q.source === "view") {
    if (!q.viewId) throw new Error("panel query source=view requires viewId");
    return buildViewReadQuery(q.viewId, {
      scope: q.scope,
      filters: q.viewFilters,
      orderBy: q.orderBy,
      // The builder takes `ascending`; the panel says `descending` so that
      // leaving it off means what a ranked panel almost always wants.
      ascending: q.orderBy !== void 0 && q.descending === false,
      limit: q.limit
    });
  }
  if (q.source === "highlights") {
    return buildHighlightsQuery({
      scope: q.scope,
      protocolId: q.protocolId,
      txId: q.txId,
      chainId: q.chainId,
      since: window.since,
      until: window.until,
      timeField: q.timeField,
      minConfidence: q.minConfidence,
      limit: q.limit
    });
  }
  return buildMetadataQuery({ ...filters, limit: q.limit });
}

// src/metadata-analytics/dashboard-edit.ts
var DASHBOARD_COLUMNS = 12;
var PANEL_DEFAULT_SIZE = {
  kpi: { w: 3, h: 2 },
  timeseries: { w: 6, h: 4 },
  toplist: { w: 6, h: 4 },
  distribution: { w: 6, h: 4 },
  table: { w: 12, h: 5 },
  // Taller than a table at the same width: its rows are one account each rather
  // than one trade, so a pool with a dozen traders is the whole panel, and it
  // carries more columns than a raw view because several are derived.
  ledger: { w: 12, h: 6 },
  highlights: { w: 12, h: 5 }
};
var EXPORTABLE_PANEL_KINDS = [
  "timeseries",
  "toplist",
  "kpi",
  "distribution",
  "table"
];
function shortId(prefix) {
  const rand = typeof globalThis.crypto?.randomUUID === "function" ? globalThis.crypto.randomUUID().replace(/-/g, "").slice(0, 12) : Math.random().toString(36).slice(2, 14);
  return `${prefix}_${rand}`;
}
var genPanelId = () => shortId("panel");
var genDashboardId = () => shortId("dash");
function newDashboard(opts) {
  return parseDashboard({
    id: genDashboardId(),
    name: opts.name,
    description: opts.description,
    scope: opts.scope ?? "tenant",
    timeRange: { preset: "24h" },
    panels: [],
    readonly: false,
    version: 1
  });
}
function cloneDashboard(dashboard, name) {
  return parseDashboard({
    ...dashboard,
    id: genDashboardId(),
    name: name ?? `${dashboard.name} (copy)`,
    scope: dashboard.scope === "system" ? "tenant" : dashboard.scope,
    readonly: false,
    version: 1,
    // Fresh panel ids so the copy's panels never alias the preset's.
    panels: dashboard.panels.map((p) => ({ ...p, id: genPanelId() }))
  });
}
function reflowPanels(panels, columns = DASHBOARD_COLUMNS) {
  let x = 0;
  let rowTop = 0;
  let rowHeight = 0;
  return panels.map((panel) => {
    const w = Math.min(Math.max(panel.position.w, 1), columns);
    const h = Math.max(panel.position.h, 1);
    if (x + w > columns) {
      rowTop += rowHeight;
      x = 0;
      rowHeight = 0;
    }
    const placed = { ...panel, position: { ...panel.position, x, y: rowTop, w, h } };
    x += w;
    rowHeight = Math.max(rowHeight, h);
    return placed;
  });
}
function appendPanel(dashboard, panel) {
  const bottom = dashboard.panels.reduce(
    (max, p) => Math.max(max, p.position.y + p.position.h),
    0
  );
  const positioned = {
    ...panel,
    position: { ...panel.position, x: 0, y: bottom }
  };
  return { ...dashboard, panels: [...dashboard.panels, positioned] };
}
function updatePanel(dashboard, panelId, patch) {
  return {
    ...dashboard,
    panels: dashboard.panels.map((p) => p.id === panelId ? { ...p, ...patch } : p)
  };
}
function removePanel(dashboard, panelId) {
  return { ...dashboard, panels: dashboard.panels.filter((p) => p.id !== panelId) };
}
function duplicatePanel(dashboard, panelId) {
  const idx = dashboard.panels.findIndex((p) => p.id === panelId);
  if (idx === -1) return dashboard;
  const original = dashboard.panels[idx];
  if (!original) return dashboard;
  const copy = {
    ...original,
    id: genPanelId(),
    title: original.title ? `${original.title} (copy)` : original.title
  };
  const panels = [...dashboard.panels];
  panels.splice(idx + 1, 0, copy);
  return { ...dashboard, panels: reflowPanels(panels) };
}
function resizePanel(dashboard, panelId, size) {
  const panels = dashboard.panels.map(
    (p) => p.id === panelId ? {
      ...p,
      position: {
        ...p.position,
        w: size.w != null ? Math.min(Math.max(size.w, 1), DASHBOARD_COLUMNS) : p.position.w,
        h: size.h != null ? Math.max(size.h, 1) : p.position.h
      }
    } : p
  );
  return { ...dashboard, panels: reflowPanels(panels) };
}
function reorderPanels(dashboard, activeId, overId) {
  if (activeId === overId) return dashboard;
  const from = dashboard.panels.findIndex((p) => p.id === activeId);
  const to = dashboard.panels.findIndex((p) => p.id === overId);
  if (from === -1 || to === -1) return dashboard;
  const panels = [...dashboard.panels];
  const [moved] = panels.splice(from, 1);
  if (!moved) return dashboard;
  panels.splice(to, 0, moved);
  return { ...dashboard, panels: reflowPanels(panels) };
}
function sourceForKind(kind) {
  return kind === "timeseries" || kind === "kpi" ? "timeseries" : "metadata";
}
function panelFromExplorer(query, draft) {
  const where = query.filters.length ? query.filters.map((f) => ({ ...f })) : void 0;
  const source = sourceForKind(draft.kind);
  let panelQuery;
  if (source === "timeseries") {
    panelQuery = {
      source: "timeseries",
      metric: draft.metric ?? "count_distinct_tx",
      // A plain series / KPI need not split; a caller may still group a series.
      groupBy: draft.groupBy,
      chainId: query.chainId,
      where
    };
  } else if (draft.kind === "toplist" || draft.kind === "distribution") {
    panelQuery = {
      source: "metadata",
      canonicalNames: draft.field ? [draft.field] : void 0,
      chainId: query.chainId,
      where,
      limit: draft.limit ?? (draft.kind === "toplist" ? 200 : 500)
    };
  } else {
    panelQuery = {
      source: "metadata",
      chainId: query.chainId,
      where,
      limit: draft.limit ?? 50
    };
  }
  return {
    id: draft.id ?? genPanelId(),
    title: draft.title,
    kind: draft.kind,
    query: panelQuery,
    position: { x: 0, y: 0, ...PANEL_DEFAULT_SIZE[draft.kind] }
  };
}
var EXPLORER_ROUTE = "/analytics/explorer";
function explorerQueryFromPanel(panel, overrides = {}) {
  const q = panel.query;
  return {
    filters: (q.where ?? []).map((f) => ({ ...f })),
    chainId: q.chainId,
    scope: overrides.scope ?? q.scope,
    minConfidence: overrides.minConfidence ?? q.minConfidence
  };
}
function explorerHrefFromPanel(panel, overrides) {
  const search = explorerQueryToSearchParams(explorerQueryFromPanel(panel, overrides)).toString();
  return `${EXPLORER_ROUTE}${search ? `?${search}` : ""}`;
}

// src/metadata-analytics/controls.ts
function mapPanels(dashboard, fn) {
  return { ...dashboard, panels: dashboard.panels.map(fn) };
}
function withMinConfidence(dashboard, minConfidence) {
  return mapPanels(dashboard, (panel) => {
    if (panel.query.source === "view") return panel;
    const query = { ...panel.query };
    if (minConfidence === void 0) {
      delete query.minConfidence;
    } else {
      query.minConfidence = minConfidence;
    }
    return { ...panel, query };
  });
}
function withTimeRange(dashboard, timeRange) {
  return { ...dashboard, timeRange };
}
function withTimeField(dashboard, timeField) {
  return mapPanels(dashboard, (panel) => {
    if (panel.query.source === "view") return panel;
    const query = { ...panel.query };
    if (timeField === void 0) {
      delete query.timeField;
    } else {
      query.timeField = timeField;
    }
    return { ...panel, query };
  });
}
function withScope(dashboard, scope) {
  return mapPanels(dashboard, (panel) => {
    const query = { ...panel.query };
    if (scope === void 0) {
      delete query.scope;
    } else {
      query.scope = scope;
    }
    return { ...panel, query };
  });
}
function variableToken(value) {
  return value.startsWith("$") ? value.slice(1) : null;
}
var TITLE_TOKEN_RE = /\$([a-zA-Z][a-zA-Z0-9_]*)/g;
function withTemplateVariables(dashboard, selections) {
  const variables = dashboard.templateVariables ?? [];
  if (!variables.length) return dashboard;
  const known = new Map(variables.map((v) => [v.name, v]));
  const resolve = (name) => {
    const variable = known.get(name);
    if (!variable) return null;
    const raw = selections[name] ?? variable.default;
    if (raw == null) return null;
    const list = (Array.isArray(raw) ? raw : [raw]).filter(
      (v) => v != null && v !== "" && v !== TEMPLATE_VARIABLE_ALL
    );
    return list.length ? list : null;
  };
  const interpolate = (text) => {
    if (!text || !text.includes("$")) return text;
    return text.replace(TITLE_TOKEN_RE, (match, name) => {
      if (!known.has(name)) return match;
      const values = resolve(name);
      return values ? values.join(", ") : "all";
    });
  };
  return mapPanels(dashboard, (panel) => {
    const query = { ...panel.query };
    const title = interpolate(panel.title) ?? panel.title;
    const description = interpolate(panel.description);
    if (query.chainId) {
      const name = variableToken(query.chainId);
      if (name && known.has(name)) {
        const values = resolve(name);
        if (!values) delete query.chainId;
        else query.chainId = values[0];
      }
    }
    if (query.viewFilters) {
      const rewritten = {};
      for (const [column, value] of Object.entries(query.viewFilters)) {
        const name = typeof value === "string" ? variableToken(value) : null;
        if (!name || !known.has(name)) {
          rewritten[column] = value;
          continue;
        }
        const values = resolve(name);
        if (values?.length) rewritten[column] = values.length > 1 ? values : values[0];
      }
      query.viewFilters = Object.keys(rewritten).length ? rewritten : void 0;
    }
    if (query.where?.length) {
      const rewritten = [];
      for (const chip of query.where) {
        const bound = chip.values.map(variableToken).find((n) => n && known.has(n));
        if (!bound) {
          rewritten.push(chip);
          continue;
        }
        const values = resolve(bound);
        if (!values) continue;
        rewritten.push({ ...chip, op: chip.op === "eq" && values.length > 1 ? "in" : chip.op, values });
      }
      query.where = rewritten.length ? rewritten : void 0;
    }
    return { ...panel, title, description, query };
  });
}
function variableScopeFilters(variables, values, name) {
  const known = new Map(variables.map((v) => [v.name, v]));
  const chips = [];
  const seen = /* @__PURE__ */ new Set([name]);
  const walk = (from) => {
    for (const dep of known.get(from)?.dependsOn ?? []) {
      if (seen.has(dep)) continue;
      seen.add(dep);
      const variable = known.get(dep);
      if (!variable) continue;
      const raw = values[dep] ?? variable.default;
      const picked = (Array.isArray(raw) ? raw : [raw]).filter(
        (v) => !!v && v !== TEMPLATE_VARIABLE_ALL
      );
      if (picked.length) {
        chips.push({
          field: variable.scopeField ?? variable.field,
          op: picked.length > 1 ? "in" : "eq",
          values: picked
        });
      }
      walk(dep);
    }
  };
  walk(name);
  return chips;
}
function dependentVariables(variables, changed) {
  const out = [];
  const queue = [changed];
  const seen = /* @__PURE__ */ new Set([changed]);
  while (queue.length) {
    const current = queue.shift();
    for (const variable of variables) {
      if (seen.has(variable.name)) continue;
      if (!(variable.dependsOn ?? []).includes(current)) continue;
      seen.add(variable.name);
      out.push(variable.name);
      queue.push(variable.name);
    }
  }
  return out;
}

// src/metadata-analytics/tenant-activity.ts
var TENANT_ACTIVITY_DASHBOARD_ID = "preset_tenant_activity";
var TALLY_SAMPLE = 500;
var TENANT_ACTIVITY_DASHBOARD = parseDashboard({
  id: TENANT_ACTIVITY_DASHBOARD_ID,
  name: "Tenant activity",
  description: "Decoded on-chain activity for the active tenant \u2014 volume, chains, protocols and actions.",
  scope: "system",
  readonly: true,
  // A week, not a day: this is the board someone opens to see whether anything
  // is happening at all, and on a 24h window a tenant whose ingest runs nightly
  // reads as a dead platform.
  timeRange: { preset: "7d" },
  panels: [
    {
      id: "tenant_kpi_transactions",
      title: "Transactions",
      // The denominator is the whole point: this is transactions the platform
      // decoded SOMETHING from, not transactions it saw. Around a quarter of all
      // traffic emits no fact at all (a bare transfer, an unmapped contract), so
      // presenting this as "transactions" without the qualifier overstates
      // coverage on every tenant.
      description: "Distinct transactions that produced at least one decoded fact.",
      kind: "kpi",
      position: { x: 0, y: 0, w: 3, h: 2 },
      query: { source: "timeseries", metric: "count_distinct_tx" }
    },
    {
      id: "tenant_kpi_facts",
      title: "Decoded facts",
      description: "Semantic facts extracted from those transactions.",
      kind: "kpi",
      position: { x: 3, y: 0, w: 3, h: 2 },
      query: { source: "timeseries", metric: "count" }
    },
    {
      id: "tenant_kpi_accounts",
      title: "Accounts",
      // `origin` (tx.from) rather than `sender`: on any router-relayed call the
      // event's sender is the router, so counting distinct senders buckets every
      // unrelated account under one address. Same choice the token-launch board
      // makes, for the same reason.
      description: "Distinct transaction signers \u2014 the account that submitted, not an event's sender.",
      kind: "kpi",
      position: { x: 6, y: 0, w: 3, h: 2 },
      query: { source: "timeseries", metric: "count_distinct_value", canonicalNames: ["origin"] }
    },
    {
      id: "tenant_kpi_protocols",
      title: "Protocols",
      description: "Distinct protocols the decode step attributed activity to.",
      kind: "kpi",
      position: { x: 9, y: 0, w: 3, h: 2 },
      query: { source: "timeseries", metric: "count_distinct_value", canonicalNames: ["protocol"] }
    },
    {
      id: "tenant_ts_by_chain",
      title: "Activity by chain",
      description: "Transactions per bucket, split by chain.",
      kind: "timeseries",
      position: { x: 0, y: 2, w: 12, h: 4 },
      // `chain_id` as a GROUP BY dimension is the EAV's own column, present on
      // every row — distinct from the `chain_id` canonical fact, which only
      // exists where a protocol definition happens to map it.
      query: { source: "timeseries", metric: "count_distinct_tx", groupBy: "chain_id" }
    },
    {
      id: "tenant_top_namespaces",
      title: "Where facts come from",
      description: "Decoded call / event / internal vs platform enrichment vs your own facets.",
      kind: "toplist",
      position: { x: 0, y: 6, w: 6, h: 4 },
      query: { source: "timeseries", metric: "count", groupBy: "namespace" }
    },
    {
      id: "tenant_top_facts",
      title: "Most-extracted facts",
      description: "Which canonical fields the platform is actually reading.",
      kind: "toplist",
      position: { x: 6, y: 6, w: 6, h: 4 },
      query: { source: "timeseries", metric: "count", groupBy: "canonical_name" }
    },
    {
      id: "tenant_top_protocols",
      title: "Top protocols",
      description: `Ranked across the ${TALLY_SAMPLE.toLocaleString()} most recent protocol attributions.`,
      kind: "toplist",
      position: { x: 0, y: 10, w: 6, h: 4 },
      query: { source: "metadata", canonicalNames: ["protocol"], limit: TALLY_SAMPLE }
    },
    {
      id: "tenant_dist_actions",
      title: "Actions",
      description: `Tallied across the ${TALLY_SAMPLE.toLocaleString()} most recent actions.`,
      kind: "distribution",
      position: { x: 6, y: 10, w: 6, h: 4 },
      query: { source: "metadata", canonicalNames: ["action"], limit: TALLY_SAMPLE }
    }
  ]
});

// src/metadata-analytics/token-launch.ts
var TOKEN_VAR = "$token";
var POOL_VAR = "$pool";
var TRADER_VAR = "$trader";
var DIRECTION_VAR = "$direction";
var TOKEN_LAUNCH_DASHBOARD = parseDashboard({
  id: "preset_token_launch",
  name: "Token launch \u2014 Overview",
  description: "Deploy, liquidity, and trading for one launched token \u2014 and whether its developer sold.",
  scope: "system",
  readonly: true,
  // A launch's interesting window is its first hours, so the board opens on a
  // range that still contains the deploy rather than one that has scrolled past
  // it. Widen it and the early trades are still there; narrow it and they are
  // the only thing there.
  timeRange: { preset: "7d" },
  // Chained token -> pool -> trader -> direction: each picker lists only values
  // that survive the ones above it. The chain is one-way, so a pool never prunes
  // the token list; see `dependsOn`.
  templateVariables: [
    {
      name: "token",
      // Lists LAUNCHED tokens (a launch that never traded is still the board's
      // subject) but scopes per leg — see `scopeField`.
      field: "launch_token",
      scopeField: "trade_token",
      label: "Token",
      multi: true
    },
    {
      name: "pool",
      // Pools that TRADED, not pools that were opened: this picker's job is to
      // pick a market to read, and the trade facts are the ones every panel
      // below it reads at the same grain.
      field: "trade_pool",
      label: "Pool",
      multi: true,
      dependsOn: ["token"]
    },
    {
      name: "trader",
      // `trade_trader`, not `origin`. Both name the signer, but only the trade
      // fact is filed per leg, so only it can be narrowed to the pool above
      // without dragging in the signers of a routed trade's other pools.
      field: "trade_trader",
      label: "Trader",
      multi: true,
      dependsOn: ["token", "pool"]
    },
    {
      name: "direction",
      // The per-trade side, not a launch role. `launch_role:swap` says a
      // transaction traded; this says which way one trade went.
      field: "trade_side",
      label: "Direction",
      multi: false,
      dependsOn: ["token", "pool", "trader"]
    }
  ],
  panels: [
    // The three trading panels read the TRADE facts, like the pickers above them.
    //
    // They used to pair `launch_pool` with `origin`, which is why a pool whose
    // eight traders are plainly in the data reported none: those facts are filed
    // per transaction and cover only the 8 transactions that also moved the
    // launch, while the pool was actually traded in 15. Selecting a trader the
    // picker offered then narrowed to a combination that cannot exist, and a
    // board that answers "0" to a question whose answer is on screen beside it is
    // worse than one that declines to answer.
    {
      id: "launch_kpi_net",
      title: "Net (ETH)",
      description: "Quote received less quote paid, over the selected legs. Negative means the selection bought more than it sold.",
      kind: "kpi",
      position: { x: 0, y: 0, w: 3, h: 2 },
      query: {
        source: "timeseries",
        // `trade_quote` is SIGNED — negative on a buy, positive on a sell — so a
        // sum is a net rather than a turnover. For one side's gross, pick a
        // Direction; for both at once, read the trades table.
        metric: "sum",
        canonicalNames: ["trade_quote"],
        where: [
          { field: "trade_token", op: "eq", values: [TOKEN_VAR] },
          { field: "trade_pool", op: "eq", values: [POOL_VAR] },
          { field: "trade_trader", op: "eq", values: [TRADER_VAR] },
          { field: "trade_side", op: "eq", values: [DIRECTION_VAR] }
        ]
      }
    },
    {
      id: "launch_kpi_traders",
      title: "Traders",
      description: "Distinct accounts that traded the pool.",
      kind: "kpi",
      position: { x: 3, y: 2, w: 3, h: 2 },
      query: {
        source: "timeseries",
        // Distinct VALUES of `trade_trader`, not distinct entities: an entity is
        // one log, so the previous metric counted legs and reported one account
        // trading twenty times as twenty traders.
        metric: "count_distinct_value",
        canonicalNames: ["trade_trader"],
        where: [
          { field: "trade_token", op: "eq", values: [TOKEN_VAR] },
          { field: "trade_pool", op: "eq", values: [POOL_VAR] },
          { field: "trade_trader", op: "eq", values: [TRADER_VAR] },
          { field: "trade_side", op: "eq", values: [DIRECTION_VAR] }
        ]
      }
    },
    // The token side of the headline, beside the ETH one rather than instead of
    // it: neither number means much alone. ETH alone cannot separate an account
    // that sold at a profit from one that sold at a loss, and tokens alone carry
    // no value. Read as a pair they classify the account — quote out and tokens
    // in is accumulation, the reverse is distribution, and a token net of about
    // zero with a positive quote net is a round trip that took ETH out, which is
    // the shape the board exists to find.
    //
    // NOT token0/token1. That ordering is the pool's, assigned by sorting the two
    // addresses, so token0 is the launched token in some pools and the money in
    // others and a column of it sums the two together. Base and quote are the
    // roles, and the roles are what the question is about.
    {
      id: "launch_kpi_net_token",
      title: "Net (token)",
      description: "Tokens received less tokens paid, over the selected legs. Positive means the selection accumulated. Meaningful for ONE token at a time \u2014 across a multi-token selection it adds different units. Empty where a token's decimals are unknown.",
      kind: "kpi",
      position: { x: 0, y: 2, w: 3, h: 2 },
      query: {
        source: "timeseries",
        // Signed OPPOSITE to `trade_quote` by construction, so the two KPIs read
        // as the two sides of the same trade rather than as two measures of it.
        metric: "sum",
        canonicalNames: ["trade_base"],
        where: [
          { field: "trade_token", op: "eq", values: [TOKEN_VAR] },
          { field: "trade_pool", op: "eq", values: [POOL_VAR] },
          { field: "trade_trader", op: "eq", values: [TRADER_VAR] },
          { field: "trade_side", op: "eq", values: [DIRECTION_VAR] }
        ]
      }
    },
    // Volume beside net, because a net cancels away the behaviour worth finding.
    // An account that bought and sold the same size nets to zero on both axes
    // and vanishes from every panel that only nets — and buying and selling the
    // same size is not nothing, it is a round trip, and at scale it is a wash.
    // Same fact as the net KPI, summed as a magnitude rather than signed.
    {
      id: "launch_kpi_volume",
      title: "Volume (ETH)",
      description: "Quote paid PLUS quote received, over the selected legs \u2014 turnover regardless of direction. A round trip that nets to zero still shows its full size here.",
      kind: "kpi",
      // Beside Net rather than under it: the pair is the reading, and the gap
      // between them is what says how much of the activity cancelled out.
      position: { x: 3, y: 0, w: 3, h: 2 },
      query: {
        source: "timeseries",
        metric: "sum_abs",
        canonicalNames: ["trade_quote"],
        where: [
          { field: "trade_token", op: "eq", values: [TOKEN_VAR] },
          { field: "trade_pool", op: "eq", values: [POOL_VAR] },
          { field: "trade_trader", op: "eq", values: [TRADER_VAR] },
          { field: "trade_side", op: "eq", values: [DIRECTION_VAR] }
        ]
      }
    },
    {
      id: "launch_kpi_trades",
      title: "Trades",
      description: "Swap legs in the selection. A trade routed through two pools counts once in each.",
      kind: "kpi",
      // Spans the KPI block's two columns so the row closes rather than leaving
      // a hole beside it.
      position: { x: 0, y: 4, w: 6, h: 2 },
      query: {
        source: "timeseries",
        // Distinct ENTITIES — one log — over a fact every leg has. Not `count`,
        // which would count facts and so report a multiple of the trades; and
        // not `count_distinct_tx`, which is the count the routed trades above
        // already distort. `trade_side` rather than `trade_quote` because a leg
        // against an unrecognised quote still happened.
        metric: "count_distinct_entity",
        canonicalNames: ["trade_side"],
        where: [
          { field: "trade_token", op: "eq", values: [TOKEN_VAR] },
          { field: "trade_pool", op: "eq", values: [POOL_VAR] },
          { field: "trade_trader", op: "eq", values: [TRADER_VAR] },
          { field: "trade_side", op: "eq", values: [DIRECTION_VAR] }
        ]
      }
    },
    {
      id: "launch_ts_volume",
      title: "Net flow (ETH)",
      description: "Signed quote per bucket: below the axis the pool was being bought, above it sold. Pick a Direction for one side's gross.",
      kind: "timeseries",
      position: { x: 6, y: 0, w: 6, h: 6 },
      query: {
        source: "timeseries",
        metric: "sum",
        canonicalNames: ["trade_quote"],
        where: [
          { field: "trade_token", op: "eq", values: [TOKEN_VAR] },
          { field: "trade_pool", op: "eq", values: [POOL_VAR] },
          { field: "trade_trader", op: "eq", values: [TRADER_VAR] },
          { field: "trade_side", op: "eq", values: [DIRECTION_VAR] }
        ]
      }
    },
    // Added and removed as two panels side by side, on the same metric and
    // scale, because a rug is only legible as the RELATION between them: seeding
    // followed by a withdrawal of the same size is the signature, and a removals
    // chart alone cannot distinguish it from an orderly exit. Two panels rather
    // than two series in one because a timeseries splits by
    // `AGGREGATE_DIMENSIONS` (canonical_name, chain_id, …) and not by a facet's
    // VALUE, so `liquidity_add` vs `liquidity_remove` cannot be one grouped query.
    {
      id: "launch_ts_liquidity_add",
      title: "Liquidity added",
      description: "A launch's liquidity arriving \u2014 the seeding a rug later reverses.",
      kind: "timeseries",
      position: { x: 0, y: 6, w: 6, h: 4 },
      query: {
        source: "timeseries",
        metric: "count_distinct_tx",
        canonicalNames: ["launch_role"],
        where: [
          { field: "launch_token", op: "eq", values: [TOKEN_VAR] },
          { field: "launch_pool", op: "eq", values: [POOL_VAR] },
          { field: "origin", op: "eq", values: [TRADER_VAR] },
          { field: "launch_role", op: "eq", values: ["liquidity_add"] }
        ]
      }
    },
    {
      id: "launch_ts_liquidity",
      title: "Liquidity removals",
      description: "A launch's liquidity leaving. The shape a rug makes.",
      kind: "timeseries",
      position: { x: 6, y: 6, w: 6, h: 4 },
      query: {
        source: "timeseries",
        metric: "count_distinct_tx",
        canonicalNames: ["launch_role"],
        where: [
          { field: "launch_token", op: "eq", values: [TOKEN_VAR] },
          { field: "launch_pool", op: "eq", values: [POOL_VAR] },
          { field: "origin", op: "eq", values: [TRADER_VAR] },
          { field: "launch_role", op: "eq", values: ["liquidity_remove"] }
        ]
      }
    },
    // Ranked on the TRADE facts, not on `origin` + `launch_role:swap`.
    //
    // That earlier pairing is what showed this pool one trader where eight had
    // traded: `launch_pool` and `launch_role` are filed per transaction, so a
    // transaction routed through two pools is credited to one of them, and 14 of
    // this pool's 15 trades were credited to another.
    //
    // `trade_pool` is filed per log, so the set of accounts here is now the
    // accounts that actually traded this pool. The bar LENGTHS are leg counts
    // rather than per-pool trade counts — a chip narrows by transaction, so a
    // routed trader's legs elsewhere still count toward their bar. For the exact
    // per-pool split, and for value rather than count, read the table below.
    // The board's headline artifact: one row per account, ranked by what it
    // actually took out.
    //
    // Reads the same view as the trades table at the bottom, folded. That table
    // is keyed per account AND side, so an account that bought and then sold is
    // two rows there — and every question worth asking is a relation between
    // those two rows rather than a property of either. Whether an account is
    // still holding, what it paid going in against what it took coming out,
    // and what it has realised are all invisible until the sides meet.
    //
    // Ranked on realised ETH rather than on volume, because volume ranks the
    // busiest address and the question is who came out ahead. An account with
    // sales it never bought — supply that arrived by mint, airdrop or transfer
    // — carries a zero cost basis and so rises to the top of exactly this list.
    {
      id: "launch_ledger",
      title: "Accounts \u2014 in, out, and what they kept",
      description: "One row per account, ranked by realised ETH. Realised is proceeds less the average cost of the tokens sold, so a partial exit is priced rather than ignored, and tokens sold that were never bought here count as pure profit. Position reads holding, closed, or distributed \u2014 the last means it sold what it did not buy in this pool. Token columns are empty where the token's decimals are unknown.",
      kind: "ledger",
      position: { x: 0, y: 10, w: 12, h: 6 },
      query: {
        source: "view",
        viewId: TOKEN_LAUNCH_VIEW_IDS.trades,
        viewFilters: {
          trade_token: TOKEN_VAR,
          trade_pool: POOL_VAR,
          trade_trader: TRADER_VAR
          // Deliberately NOT bound to `$direction`. The fold needs both sides of
          // an account to say anything, and a board filtered to buys would
          // render every account as though it had never sold.
        },
        limit: 500
      }
    },
    {
      id: "launch_top_traders",
      title: "Top traders",
      description: "Accounts that traded this pool, by how often they appear. Exact per-pool amounts are in the trades table.",
      kind: "toplist",
      position: { x: 0, y: 16, w: 6, h: 4 },
      query: {
        source: "metadata",
        canonicalNames: ["trade_trader"],
        where: [
          // `trade_token`, not `launch_token`: the trade facts carry their own
          // token so a launch trading across several pools narrows to the launch
          // rather than to one venue of it.
          { field: "trade_token", op: "eq", values: [TOKEN_VAR] },
          { field: "trade_pool", op: "eq", values: [POOL_VAR] },
          { field: "trade_trader", op: "eq", values: [TRADER_VAR] }
        ],
        limit: 500
      }
    },
    {
      id: "launch_dist_role",
      title: "Activity by role",
      description: "Deploy, pool open, liquidity, trade \u2014 kept apart on purpose.",
      kind: "distribution",
      position: { x: 6, y: 16, w: 6, h: 4 },
      query: {
        source: "metadata",
        canonicalNames: ["launch_role"],
        where: [
          { field: "launch_token", op: "eq", values: [TOKEN_VAR] },
          { field: "launch_pool", op: "eq", values: [POOL_VAR] },
          { field: "origin", op: "eq", values: [TRADER_VAR] }
        ],
        limit: 500
      }
    },
    {
      id: "launch_table_launches",
      title: "Launches",
      description: "One row per pool a launch trades in: the quote asset, the pool, and who deployed the token.",
      kind: "table",
      position: { x: 0, y: 20, w: 12, h: 5 },
      query: {
        source: "view",
        viewId: TOKEN_LAUNCH_VIEW_IDS.launch,
        // A view is filtered by COLUMN rather than by `where` chip, which is why
        // the binding is spelled here and not above — and a column exists only if
        // the view groups by it (`LAUNCH_INVENTORY_GROUP_BY` in the registry
        // presets). Both of these do.
        viewFilters: { launch_token: TOKEN_VAR, launch_pool: POOL_VAR },
        limit: 50
      }
    },
    {
      id: "launch_table_trading",
      title: "Trading by role",
      description: "Each pool's activity split by the part played \u2014 a dev pulling liquidity never averages into a buy.",
      kind: "table",
      position: { x: 0, y: 25, w: 12, h: 5 },
      query: {
        source: "view",
        viewId: TOKEN_LAUNCH_VIEW_IDS.trading,
        viewFilters: { launch_token: TOKEN_VAR, launch_pool: POOL_VAR },
        limit: 50
      }
    },
    // The tape: one row per swap leg, and the bottom of the board's drill-down.
    //
    // The three levels above it each answer a different question and none can
    // answer this one. The KPIs total the selection, the ledger folds it to one
    // row per account, and both of them aggregate the transaction away — so the
    // moment a number looks wrong, "which transaction was that" is unanswerable
    // from the panel showing it. This is where a reader gets the hash and checks
    // it against a block explorer.
    //
    // It replaces a roll-up of the same view keyed on account AND direction. The
    // ledger above now gives every figure that one did — gross in, gross out and
    // the net — per account rather than per account and side, so the old panel
    // was two rows to add up by hand for strictly less than the tape offers.
    //
    // A FLAT view rather than a grouped one, which is what carries `tx_id` and
    // `entity_id` without either being declared: with no `group_by`, the pivot
    // is at entity grain, and for these facts an entity is one swap leg. Two
    // things follow. The amounts are the exact decimal strings the pipeline
    // wrote rather than a summed double — which matters on the one panel meant
    // to be reconciled digit for digit. And a routed order appears as what it
    // is: transaction 0x09586361…71c2 bought TOKI through a V2 pair AND a V3
    // pool, and the tape shows two rows agreeing on the hash where the roll-up
    // showed one pool's share with nothing to say the other existed.
    {
      id: "launch_table_trades",
      title: "Trade tape",
      description: "One row per swap leg, newest first \u2014 the transaction it happened in, the pool, the account, and the exact amounts. A routed order shows one row per pool it touched, all sharing a hash. Amounts are unrounded here, so this is the panel to reconcile against a block explorer.",
      kind: "table",
      position: { x: 0, y: 30, w: 12, h: 6 },
      query: {
        source: "view",
        viewId: TOKEN_LAUNCH_VIEW_IDS.tradeEvents,
        // Keyed on the trade's OWN token and pool rather than the launch stamp's:
        // the columns exist because the facts are per log, which is what lets the
        // launch be narrowed across its pools and each pool be read exactly.
        //
        // Direction belongs here, unlike on the ledger: a row IS one trade, so
        // filtering to buys hides rows rather than misreporting the ones left.
        viewFilters: {
          trade_token: TOKEN_VAR,
          trade_pool: POOL_VAR,
          trade_trader: TRADER_VAR,
          trade_side: DIRECTION_VAR
        },
        // Newest first. Without an explicit order a LIMIT is not a "latest N" —
        // SQL leaves which rows survive entirely to the engine, so the panel
        // would show an arbitrary hundred and imply they were the recent ones.
        orderBy: "block_timestamp",
        descending: true,
        limit: 100
      }
    }
  ]
});
function scopeChip(scope) {
  return {
    field: scope.kind === "token" ? "launch_token" : "launch_pool",
    op: "eq",
    values: [scope.value.toLowerCase()]
  };
}
function launchTradingBoard(scope) {
  const chip = scopeChip(scope);
  const subject = scope.kind === "token" ? "token" : "pool";
  return parseDashboard({
    id: `embed_launch_trading_${scope.kind}`,
    name: "Trading",
    description: `Volume, traders and trade counts for this ${subject}.`,
    scope: "system",
    readonly: true,
    timeRange: { preset: "7d" },
    panels: [
      {
        id: "embed_volume",
        title: "Volume (USD)",
        description: "Summed over priced legs \u2014 empty until a price provider is wired.",
        kind: "kpi",
        position: { x: 0, y: 0, w: 3, h: 2 },
        query: {
          source: "timeseries",
          metric: "sum",
          canonicalNames: ["value_usd"],
          where: [chip, { field: "launch_role", op: "eq", values: ["swap"] }]
        }
      },
      {
        id: "embed_traders",
        title: "Traders",
        description: "Distinct accounts that traded. One account swapping ten times is one.",
        kind: "kpi",
        position: { x: 3, y: 0, w: 3, h: 2 },
        query: {
          source: "timeseries",
          // Distinct VALUES of `origin`, which is what the description above
          // claims. Distinct ENTITIES is distinct transactions, so the same
          // account swapping ten times counted ten.
          metric: "count_distinct_value",
          canonicalNames: ["origin"],
          where: [chip, { field: "launch_role", op: "eq", values: ["swap"] }]
        }
      },
      {
        id: "embed_trades",
        title: "Trades",
        kind: "timeseries",
        position: { x: 6, y: 0, w: 6, h: 4 },
        query: {
          source: "timeseries",
          // Counted by transaction, not by fact: one swap writes several rows
          // (its role, its origin, its value), and counting rows would report a
          // multiple of the trades that happened.
          metric: "count_distinct_tx",
          canonicalNames: ["launch_role"],
          where: [chip, { field: "launch_role", op: "eq", values: ["swap"] }]
        }
      },
      {
        id: "embed_top_traders",
        title: "Top traders",
        description: "Who moved the most value. The first names here are the snipers.",
        kind: "toplist",
        position: { x: 0, y: 2, w: 6, h: 4 },
        query: {
          source: "metadata",
          canonicalNames: ["origin"],
          where: [chip, { field: "launch_role", op: "eq", values: ["swap"] }],
          limit: 500
        }
      }
    ]
  });
}

// src/metadata-analytics/ccip.ts
var CCIP_OVERVIEW_DASHBOARD = parseDashboard({
  id: "preset_ccip_overview",
  name: "Chainlink CCIP \u2014 Overview",
  description: "Cross-chain message flow, chains, and fees for Chainlink CCIP.",
  scope: "system",
  readonly: true,
  timeRange: { preset: "7d" },
  panels: [
    {
      id: "ccip_kpi_messages",
      title: "Messages",
      kind: "kpi",
      position: { x: 0, y: 0, w: 3, h: 2 },
      query: { source: "timeseries", metric: "count_distinct_tx", canonicalNames: ["message_id"] }
    },
    {
      id: "ccip_kpi_facts",
      title: "Fee events",
      kind: "kpi",
      position: { x: 3, y: 0, w: 3, h: 2 },
      query: { source: "timeseries", metric: "count", canonicalNames: ["fee_amount"] }
    },
    {
      id: "ccip_ts_volume",
      title: "Message volume",
      kind: "timeseries",
      position: { x: 6, y: 0, w: 6, h: 4 },
      query: { source: "timeseries", metric: "count_distinct_tx", canonicalNames: ["message_id"] }
    },
    {
      id: "ccip_top_source_chains",
      title: "Top source chains",
      kind: "toplist",
      position: { x: 0, y: 2, w: 6, h: 4 },
      query: {
        source: "timeseries",
        metric: "count_distinct_tx",
        groupBy: "chain_id",
        canonicalNames: ["message_id"]
      }
    },
    {
      id: "ccip_dist_dest_chain",
      title: "Destination chains",
      kind: "distribution",
      position: { x: 0, y: 6, w: 6, h: 4 },
      query: { source: "metadata", canonicalNames: ["dest_chain"], limit: 500 }
    },
    {
      id: "ccip_dist_fee_token",
      title: "Fee token",
      kind: "distribution",
      position: { x: 6, y: 6, w: 6, h: 4 },
      query: { source: "metadata", canonicalNames: ["fee_token"], limit: 500 }
    },
    {
      id: "ccip_table_recent",
      title: "Recent messages",
      kind: "table",
      position: { x: 0, y: 10, w: 12, h: 5 },
      query: { source: "view", viewId: CCIP_VIEW_IDS.message, limit: 50 }
    },
    {
      id: "ccip_highlights",
      title: "Key attributes",
      description: "Render-ready highlights for recent CCIP messages.",
      kind: "highlights",
      position: { x: 0, y: 15, w: 12, h: 5 },
      query: { source: "highlights", protocolId: CCIP_PROTOCOL_ID, limit: 20 }
    }
  ]
});
var PRESET_DASHBOARDS = {
  [CCIP_OVERVIEW_DASHBOARD.id]: CCIP_OVERVIEW_DASHBOARD,
  [TOKEN_LAUNCH_DASHBOARD.id]: TOKEN_LAUNCH_DASHBOARD,
  // The Stats page's board. Registered here like the other two so it also shows
  // on the Analytics shelf and can be cloned into an editable copy — a tenant
  // that wants its own cut of the same numbers starts from this rather than
  // from an empty grid.
  [TENANT_ACTIVITY_DASHBOARD.id]: TENANT_ACTIVITY_DASHBOARD
};

// src/metadata-analytics/protocols.ts
var SYSTEM_ONLY_PATCH_FIELDS = [
  "protocol_id",
  "protocol_version",
  "category",
  "namespace",
  "handler_module",
  "decoder_module",
  "resolution"
];
function linkageSagaKinds(detail) {
  const raw = detail?.definition?.["cross_chain_linkage"];
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const kind = entry.saga_kind;
    if (typeof kind === "string" && kind && !out.includes(kind)) out.push(kind);
  }
  return out;
}
function parseDefinitionPatch(raw) {
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    return {
      ok: false,
      rejection: { kind: "unparseable", detail: err.message }
    };
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { ok: false, rejection: { kind: "not_an_object" } };
  }
  const patch = parsed;
  const keys = Object.keys(patch);
  if (keys.length === 0) return { ok: false, rejection: { kind: "empty" } };
  const denied = keys.filter(
    (k) => SYSTEM_ONLY_PATCH_FIELDS.includes(k)
  );
  if (denied.length) {
    return { ok: false, rejection: { kind: "system_only", fields: denied } };
  }
  return { ok: true, patch };
}
function describePatchRejection(rejection) {
  switch (rejection.kind) {
    case "unparseable":
      return `That isn't valid JSON: ${rejection.detail}`;
    case "not_an_object":
      return "An override is a map of definition fields, so it has to be a JSON object.";
    case "empty":
      return "An empty override is a delete \u2014 remove the override instead.";
    case "system_only":
      return `${rejection.fields.join(", ")} can only be set by the system definition, so an override can't change ${rejection.fields.length > 1 ? "them" : "it"}.`;
  }
}
function buildProtocolsQuery(scope) {
  return { path: configPaths.protocols(), query: scopeQuery(scope) };
}
function buildProtocolQuery(protocolId, scope) {
  return { path: configPaths.protocol(protocolId), query: scopeQuery(scope) };
}
function protocolOverrideRequest(protocolId, patch, scope) {
  return {
    path: configPaths.protocol(protocolId),
    query: scopeQuery(scope),
    body: { definition_patch: patch }
  };
}
function protocolOverrideDeleteRequest(protocolId, scope) {
  return { path: configPaths.protocol(protocolId), query: scopeQuery(scope) };
}
function protocolsPath() {
  return METADATA_PATHS.protocols;
}

export { AGGREGATE_DIMENSIONS, AGGREGATE_METRICS, AGGREGATE_METRIC_LABELS, ANALYTICS_SCOPES, ANALYTICS_SCOPE_LABELS, CCIP_FACETS, CCIP_OVERVIEW_DASHBOARD, CCIP_PROTOCOL_ID, CCIP_VIEW_IDS, CONFIDENCE_PRESETS, DASHBOARD_COLUMNS, DEFAULT_ANALYTICS_LIMIT, DEFAULT_ANALYTICS_SCOPE, DEFAULT_TIME_FIELD, DashboardPanelSchema, DashboardSchema, EMPTY_EXPLORER_QUERY, EXPLORER_ROUTE, EXPORTABLE_PANEL_KINDS, FILTER_OPS, FILTER_OP_LABELS, INTERNAL_ANALYTICS_SCOPES, METADATA_PATHS, MIN_CONFIDENCE_BOUNDS, NUMERIC_AGGREGATE_METRICS, PANEL_DEFAULT_SIZE, PANEL_KINDS, PANEL_SOURCES, PRESET_DASHBOARDS, PRESET_LABELS, PRESET_SECONDS, PanelFilterSchema, PanelPositionSchema, PanelQuerySchema, RANGE_FILTER_OPS, SELF_SERVICE_ANALYTICS_SCOPES, SYSTEM_ONLY_PATCH_FIELDS, TEMPLATE_VARIABLE_ALL, TENANT_ACTIVITY_DASHBOARD, TENANT_ACTIVITY_DASHBOARD_ID, TIME_FIELDS, TIME_FIELD_HINTS, TIME_FIELD_LABELS, TIME_RANGE_PRESETS, TOKEN_LAUNCH_DASHBOARD, TOKEN_LAUNCH_FACETS, TOKEN_LAUNCH_VIEW_IDS, TRADE_SIDES, TemplateVariableSchema, TimeRangeSchema, appendAndClause, appendPanel, buildCatalogQuery, buildFacetValuesQuery, buildHighlightsQuery, buildMetadataQuery, buildPanelQuery, buildProtocolQuery, buildProtocolsQuery, buildTimeseriesQuery, buildTransactionDetailQuery, buildTransactionsQuery, buildViewReadQuery, cloneDashboard, collectLeafFilters, configPaths, decodeFilter, definitionOverrideScope, dependentVariables, describePatchRejection, duplicatePanel, encodeFilter, equalsFilter, explorerHrefFromPanel, explorerQueryFromPanel, explorerQueryFromSearchParams, explorerQueryToSearchParams, facetCreateBody, filterParams, formatExplorerQuery, formatFilter, formatQuery, genDashboardId, genPanelId, humanizeFieldName, isNumericMetric, launchTradingBoard, linkageSagaKinds, materializeRequest, newDashboard, panelFromExplorer, parseDashboard, parseDefinitionPatch, parseExplorerQuery, parseQuery, parseTimeValue, protocolOverrideDeleteRequest, protocolOverrideRequest, protocolsPath, queryTextToExplorer, quoteQueryValue, reflowPanels, removePanel, renderFieldValue, reorderPanels, resizePanel, resolveTimeRange, scopeQuery, shortenHex, suggestBucketSeconds, treeToFilters, unwrapValue, updatePanel, variableScopeFilters, viewCreateBody, whereParams, withFilter, withMinConfidence, withScope, withTemplateVariables, withTimeField, withTimeRange, withoutFilter };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map